import { Link } from "react-router-dom";

import { helpArticleById } from "./corpus";

/**
 * Контекстные ссылки «Подробнее» (E16, §3 плана).
 *
 * ГРАНИЦА, ЗАПИСАННАЯ В ПЛАНЕ: ссылка ставится ТОЛЬКО у ДОМЕННОГО отказа — там, где человеку
 * уже сказано по-русски, что произошло («черновик в работе у коллеги», «в архив нельзя, пока
 * есть незакрытые обязательства»). Общий отказ в правах (`403 forbidden`) в E16 не трогается:
 * он молчалив намеренно (контрольный пример 15 ТЗ наблюдает порядок ответов 401/403), и
 * приделать к нему объяснение — это работа про права, а не про документацию.
 *
 * ПОЧЕМУ ССЫЛКИ ПЕРЕЧИСЛЕНЫ ЗДЕСЬ, А НЕ ВПИСАНЫ ПО МЕСТУ. Адрес статьи, набранный строкой в
 * экране, живёт ровно до переименования статьи — и после него ведёт в «статья не найдена»,
 * о чём никто не узнает. Список в одном месте проверяется тестом целиком: каждая ссылка
 * разрешается в существующую статью корпуса.
 */
export const HELP_LINKS = {
  /** Черновик ИИ-разбора занят другим юристом — подтвердить и отклонить сейчас нельзя. */
  draftHeldByOther: "ai-razbor-i-verifikaciya",
  /**
   * Комплект уже взят юристом в работу — отправитель отозвать его не может, кнопки нет.
   * (Отказ «отозвать можно только СВОЙ комплект» живёт на сервере и в интерфейс не выводится:
   * чужую кнопку отзыва там просто не показывают, объяснять нечего.)
   */
  draftWithdrawBlockedByLawyer: "ai-razbor-i-verifikaciya",
  /** Договор не уходит в архив: есть незакрытые обязательства. */
  contractArchiveBlocked: "dogovor-i-specifikacii",
  /** Событие исполнения не правится и не удаляется — только сторнирование. */
  eventReversalOnly: "sobytiya-ispolneniya-i-storno",
  /** Файл не принят: не тот тип или больше 50 МБ (и заодно — почему нет удаления). */
  fileUploadRejected: "fayly-i-skany",
  /** Потолок неустойки не применён: не заполнена база (цена договора или сумма спецификации). */
  penaltyCapBaseMissing: "neustoyka-i-potolok",
  /** Претензия не правится: ошибочная отменяется, заводится новая. */
  claimNotEditable: "pretenzii-i-iskovaya-davnost",
} as const;

/**
 * ПОЧЕМУ ЗДЕСЬ НЕТ ССЫЛКИ С ЭКРАНА ПРИНУДИТЕЛЬНОЙ СМЕНЫ ПАРОЛЯ, хотя отказ там доменный и
 * человеческий текст у него есть. Пока пароль не сменён, гард уводит на смену пароля с любого
 * другого адреса — «Подробнее» вернуло бы человека на тот же экран. Это был бы тупик, а тупиков
 * в справке нет по построению (план §5, п. 8). Статья про вход открывается из оглавления.
 */

export type HelpLinkKey = keyof typeof HELP_LINKS;

/**
 * Ссылка «Подробнее» рядом с текстом отказа. Заголовок статьи подставляется в `title`, чтобы
 * человек понимал, куда его уводят, до нажатия.
 */
export function HelpLink({ topic, className }: { topic: HelpLinkKey; className?: string }) {
  const id = HELP_LINKS[topic];
  const article = helpArticleById(id);
  return (
    <Link
      to={`/help/${id}`}
      title={article === null ? undefined : `Справка: ${article.title}`}
      className={`text-accent underline ${className ?? ""}`.trim()}
    >
      Подробнее
    </Link>
  );
}
